"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { Clock3, Search } from "lucide-react";
import { domId } from "@/lib/dom-id";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export interface SidebarSearchItem {
  id: string;
  name: string;
  description?: string;
  href: string;
  type: "system" | "mcp" | "action";
  updatedLabel: string;
  updatedAt: number;
}

interface SidebarSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: SidebarSearchItem[];
  onSearch: (query: string) => Promise<SidebarSearchItem[]>;
}

function normalizeSearchName(value: string) {
  return value.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ").trim();
}

function rankSearchResults(items: SidebarSearchItem[], query: string) {
  const normalizedQuery = normalizeSearchName(query);
  const words = normalizedQuery.split(" ").filter(Boolean);

  return items.map(item => {
    const name = normalizeSearchName(item.name);
    // Exact names precede partial names and description-only matches. Within
    // each tier, connectors precede actions so their children cannot hide them.
    const matchRank = name === normalizedQuery ? 0
      : words.every(word => name.includes(word)) ? 2 : 4;
    return {item, rank: words.length ? matchRank + (item.type === "action" ? 1 : 0) : 0};
  }).sort((left, right) => left.rank - right.rank || right.item.updatedAt - left.item.updatedAt)
    .slice(0, 6).map(({item}) => item);
}

export default function SidebarSearchModal({
  open,
  onOpenChange,
  items,
  onSearch,
}: SidebarSearchModalProps) {
  const t = useTranslations("Sidebar.searchModal");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SidebarSearchItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
      setIsSearching(false);
    }
  }, [open]);

  useEffect(() => {
    const trimmedQuery = query.trim();
    if (!open || trimmedQuery.length === 0) {
      setResults([]);
      setIsSearching(false);
      return;
    }

    let cancelled = false;
    setIsSearching(true);

    const timeoutId = window.setTimeout(async () => {
      try {
        const nextResults = await onSearch(trimmedQuery);
        if (!cancelled) {
          setResults(rankSearchResults(nextResults, trimmedQuery));
        }
      } catch (error) {
        console.error("Sidebar search failed", error);
        if (!cancelled) {
          setResults([]);
        }
      } finally {
        if (!cancelled) {
          setIsSearching(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [onSearch, open, query]);

  const showResults = query.trim().length > 0;
  const displayedItems = showResults ? results : items.slice(0, 6);
  const trimmedQuery = query.trim();

  function highlightText(text: string, highlight: string, tone: "title" | "description") {
    if (!highlight) {
      return text;
    }

    const escaped = highlight.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${escaped})`, "ig"));

    return parts.map((part, index) => {
      const isMatch = part.toLowerCase() === highlight.toLowerCase();
      return isMatch ? (
        <span
          key={`${part}-${index}`}
          className={cn(
            tone === "title"
              ? "text-[#6D28D9]"
              : "text-[#6D28D9]",
          )}
        >
          {part}
        </span>
      ) : (
        <span key={`${part}-${index}`}>{part}</span>
      );
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showClose={false}
        className="w-[600px] max-w-[calc(100vw-32px)] rounded-2xl p-12"
      >
        <DialogTitle className="sr-only">{t("title")}</DialogTitle>

        <div className="flex h-[504px] w-full flex-col gap-4">
          <label className="sr-only" htmlFor="sidebar-search-input">
            {t("inputLabel")}
          </label>
          <div className="flex h-12 w-full items-center gap-4 rounded-lg border border-[#4C1D95] bg-white py-3 pl-5 pr-3">
            <input
              id="sidebar-search-input"
              data-floating-field-control="true"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              autoFocus
              placeholder={t("placeholder")}
              className="flex-1 appearance-none border-0 bg-transparent text-sm font-normal leading-5 text-[#18181B] shadow-none outline-none ring-0 placeholder:text-[#9CA3AF] focus:border-0 focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
            />
            <span className="flex items-center justify-center rounded-full p-1 text-[#444746]">
              <Search className="h-5 w-5" />
            </span>
          </div>

          <div className="flex min-h-0 w-full flex-1 flex-col gap-2">
            <div className="flex min-h-0 w-full flex-1 flex-col gap-2">
              <div className="flex w-full flex-col gap-1 px-4">
                <h2 className="text-lg font-medium leading-6 text-[#444746]">
                  {showResults ? t("results") : t("recent")}
                </h2>
                {showResults ? (
                  <p className="text-sm font-normal leading-5 text-[#444746]">
                    {t("resultsCount", {
                      count: displayedItems.length,
                      query: trimmedQuery,
                    })}
                  </p>
                ) : null}
              </div>

              {isSearching ? (
                <div className="flex w-full flex-col gap-3 px-4 py-2">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 animate-pulse rounded-full bg-[#EEF2FF]" />
                    <div className="flex flex-1 flex-col items-start gap-2">
                      <div className="h-5 w-36 animate-pulse rounded-3xl bg-[#EEF2FF]" />
                      <div className="h-3 w-full animate-pulse rounded-3xl bg-[#EEF2FF]" />
                    </div>
                  </div>
                </div>
              ) : displayedItems.length === 0 ? (
                <div className="flex flex-1 items-center justify-center px-4 py-4">
                  <div className="flex w-96 flex-col items-center gap-4">
                    <div className="relative h-64 w-64">
                      <Image
                        src="/search_empty_state.svg"
                        alt=""
                        fill
                        className="object-contain"
                        sizes="256px"
                      />
                    </div>
                    <p className="text-center text-base font-normal leading-6 text-[#444746]">
                      {showResults ? t("emptyResults") : t("emptyRecent")}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex min-h-0 w-full flex-1 flex-col gap-1 overflow-y-auto">
                  {displayedItems.map((item) => {
                    const isRecent = !showResults;
                    return (
                      <Link
                        key={item.id}
                        href={item.href}
                        data-testid={domId("lnk", `sidebar-search-item-${item.id}`)}
                        onClick={() => onOpenChange(false)}
                        className="group flex w-full items-start justify-between gap-2 rounded-lg px-4 py-2 text-left transition-colors hover:bg-[#EEF2FF]"
                      >
                        <div className="flex min-w-0 flex-1 flex-col gap-1 opacity-95">
                          <div className="flex items-start justify-between gap-4">
                            <span className="min-w-0 text-sm font-medium leading-[1.4] text-[#0C0A09]">
                              {showResults
                                ? highlightText(item.name, trimmedQuery, "title")
                                : item.name}
                            </span>
                            <div
                              className={cn(
                                "ml-4 flex shrink-0 items-center gap-1 text-[#9CA3AF]",
                                isRecent
                                  ? "opacity-0 transition-opacity group-hover:opacity-100"
                                  : "",
                              )}
                            >
                              <Clock3 className="h-3 w-3" />
                              <span className="text-[10px] font-normal leading-3">
                                {item.updatedLabel}
                              </span>
                            </div>
                          </div>
                          {!isRecent && item.description ? (
                            <span className="line-clamp-1 text-xs font-normal leading-4 text-[#444746]">
                              {showResults
                                ? highlightText(
                                    item.description,
                                    trimmedQuery,
                                    "description",
                                  )
                                : item.description}
                            </span>
                          ) : null}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
